import { useRef, useState } from "react";
import { Grid } from "@mui/material";

import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { Layout } from "ui-components/Layout";
import { FormTitle, SelectFields, ReadonlyText } from "ui-components/Form";
import { Success, Error } from "ui-components/Alert";

import { getPutaway, editPutaway } from "services/data.inbound.service";
import { useLocationOptionsQuery } from "hooks/useLocationQuery";
import { putawayColumns } from "./columns";
import { BackButton } from "ui-components/Button";
import { StripedCheckboxDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { ErrorAlert, LoadingDialog } from "ui-components/Alert";
import { useZoneOptionsQuery } from "hooks/useQueryHook";

function EditPutaway() {
  const location = useLocation();
  const navigate = useNavigate();
  const { putawayId } = location?.state ?? {};
  //const putawayId = 1;
  const [alert, setAlert] = useState(null);
  const [putawayHeader, setPutawayHeader] = useState(null);
  const [putawayDetail, setPutawayDetail] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [locOptions, setLocOptions] = useState([]);
  const [zoneObj, setZoneObj] = useState({});
  const [zoneID, setZoneID] = useState(null);
  const [locID, setLocID] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const onSuccess = (data) => setAlert(<Success data={data} onChange={setAlert} />);
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);
  const [editAlert, setEditAlert] = useState(null);
  const { isFetching } = useQuery({
    queryKey: ["putaway", putawayId],
    queryFn: () => getPutaway({ putawayId }),
    onSuccess: (data) => {
      const { putawayDetails, putawayZones, ...values } = data.data.data;
      setPutawayHeader(values);
      setPutawayDetail(putawayDetails);
      let obj = {};
      putawayZones.forEach((x) => {
        obj[x.zoneID] = x.locations;
      });
      setZoneObj(obj);
    },
    onError,
  });
  const editPutawayMutation = useMutation(editPutaway, {});
  useZoneOptionsQuery({
    whCode: putawayHeader?.warehouseCode,
    zoneType: "storage/quarantine",
    onSuccess: (data) => setZoneOptions(data.data.data),
  });

  useLocationOptionsQuery({
    whCode: putawayHeader?.warehouseCode,
    zoneID: zoneID,
    onSuccess: (data) => {
      const { data: locations } = data.data;
      setLocID(null);
      setLocOptions(locations);
      if (!zoneObj.hasOwnProperty(zoneID)) {
        setZoneObj({ ...zoneObj, [zoneID]: locations });
      }
    },
  });
  const apply = () => {
    if (zoneID === null || locID === null) return;
    if (selectedIds.length === 0) return;
    const updatedDetail = putawayDetail.map((x) => {
      if (selectedIds.includes(x.id)) {
        x.toZone = zoneID;
        x.toLocation = locID;
      }
      return x;
    });

    setPutawayDetail(updatedDetail);
    setSelectedIds([]);
    setEditAlert(null);
    setAlert(
      <Success
        content={`Line No [${selectedIds.join(",")}] To Zone and To Location Updated in data grid`}
        onChange={setAlert}
      />
    );
  };
  //const [isSubmitting, setIsSubmitting] = useState(false);
  const editStatus = editPutawayMutation.status === "loading";

  console.log(zoneObj);

  const submit = (confirmPutaway) => {
    //setIsSubmitting(true);
    const isAnyEmptyValue = putawayDetail.some((x) => x.toLocation === null);
    if (isAnyEmptyValue) return;

    editPutawayMutation.mutate(
      {
        putawayId,
        req: { putawayDetails: putawayDetail, confirmPutaway },
      },
      {
        onSuccess: (data) => {
          if (data.data.code === 200) {
            navigate("/inbound/putaway", { state: confirmPutaway ? "P" : "O" });
          } else {
            window.scrollTo(0, 0);
            setEditAlert(<ErrorAlert content={data.data.message} />);
          }
        },
        onError: (data) => {
          window.scrollTo(0, 0);
          setAlert(<Error data={data} onChange={setAlert} />);
        },
      }
    );
  };

  return (
    <Layout lg={12}>
      {alert}
      {(isFetching || editStatus) && <LoadingDialog />}
      <FormTitle title="Putaway" />
      <MDBox mt={1.625}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <ReadonlyText
              field={{
                label: "Putaway Number",
                name: "putawayNumber",
                value: putawayHeader?.putawayNumber,
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <ReadonlyText
              field={{ label: "Receving Code", name: "recvCode", value: putawayHeader?.recvCode }}
            />
          </Grid>
          <Grid item xs={3}>
            <ReadonlyText
              field={{
                label: "Customer Code",
                name: "customerCode",
                value: putawayHeader?.customerCode,
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <ReadonlyText
              field={{ label: "Vendor Code", name: "vendorCode", value: putawayHeader?.vendorCode }}
            />
          </Grid>
          <Grid item xs={3}>
            <ReadonlyText
              field={{
                label: "Warehouse Code",
                name: "warehouseCode",
                value: putawayHeader?.warehouseCode,
              }}
            />
          </Grid>
        </Grid>
      </MDBox>

      <MDBox mt={5}>
        <FormTitle title="Edit Putaway Detail" />
        {editAlert}
        <MDBox mt={1.625}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <SelectFields
                options={zoneOptions}
                field={{ label: "To Zone", name: "toZone", errors: {} }}
                onChange={(e, option) => {
                  setZoneID(option);
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <SelectFields
                options={locOptions}
                field={{ label: "To Location", name: "toLocation", errors: {} }}
                onChange={(e, option) => {
                  setLocID(option);
                }}
              />
            </Grid>
            <Grid item>
              <MDButton
                variant="gradient"
                color="info"
                onClick={apply}
                disabled={zoneID === null || locID === null || selectedIds.length === 0}
              >
                apply
              </MDButton>
            </Grid>
          </Grid>
          <MDTypography mt={1.625} mb={2} color="info" variant="h5">
            Datagrid Display
          </MDTypography>
          <StripedCheckboxDataGrid
            columns={putawayColumns(zoneOptions, zoneObj)}
            rows={putawayDetail}
            onRowSelectionModelChange={(id) => setSelectedIds(id)}
            rowSelectionModel={selectedIds}
            processRowUpdate={(updatedRow, originalRow) => {
              if (zoneObj.hasOwnProperty(updatedRow.toZone)) {
                console.log("exists");
              } else {
                setZoneID(updatedRow.toZone);
              }
              setPutawayDetail(
                putawayDetail.map((x) => {
                  if (x.id === updatedRow.id) {
                    if (updatedRow.toZone !== originalRow.toZone) {
                      updatedRow.toLocation = null;
                    }
                    return updatedRow;
                  }
                  return x;
                })
              );
              return updatedRow;
            }}
            onProcessRowUpdateError={(error) => console.log(error)}
          />
        </MDBox>
      </MDBox>

      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton variant="gradient" color="info" onClick={() => submit(false)}>
          save
        </MDButton>
        <MDButton
          sx={{ marginLeft: "1.5rem" }}
          variant="gradient"
          color="info"
          onClick={() => submit(true)}
        >
          save & confirm putaway
        </MDButton>
        <BackButton label="Back" url="/inbound/putaway" state="O" />
      </MDBox>
    </Layout>
  );
}

export default EditPutaway;
